import { z } from 'zod';

// Saldo y movimientos (RF-02.01, RF-02.02). Los créditos son enteros (RN-002).

export const balanceSchema = z
  .object({
    available: z.number().int(),
    /** Retenido por operaciones en curso o en revisión. */
    held: z.number().int(),
    /** Bloqueado por membresía (RN-005): se conserva, no se puede usar. */
    frozen: z.number().int(),
  })
  .meta({ id: 'Balance' });

export const movementTypeSchema = z.enum([
  'purchase',
  'promotion',
  'adjustment',
  'reservation',
  'settlement',
  'release',
  'freeze',
  'unfreeze',
  'legacy_import',
  'refund',
]);

export const movementSchema = z.object({
  id: z.uuid(),
  type: movementTypeSchema,
  available: z.number().int(),
  held: z.number().int(),
  frozen: z.number().int(),
  reference: z.string().nullable(),
  reason: z.string(),
  createdAt: z.iso.datetime(),
});

export type Movement = z.infer<typeof movementSchema>;

export const adjustmentRequestSchema = z.object({
  /** Positivo suma y negativo resta créditos del disponible. */
  amount: z.number().int(),
  reason: z.string().max(500),
});

export const adjustmentResponseSchema = z.object({
  transactionId: z.uuid(),
  balance: balanceSchema,
});

// Recargas con Stripe Checkout (RF-02.04 a RF-02.09, A-13).

export const creditPackSchema = z
  .object({
    id: z.string(),
    credits: z.number().int(),
    /** Precio del pack en céntimos. */
    amountCents: z.number().int(),
    currency: z.literal('eur'),
    /** `false`: Stripe suma el IVA que corresponda al pagar. */
    taxIncluded: z.boolean(),
  })
  .meta({ id: 'CreditPack' });

export const creditPacksSchema = z.object({
  /** `false` si las recargas no están disponibles (sin Stripe configurado). */
  enabled: z.boolean(),
  items: z.array(creditPackSchema),
});

export const checkoutRequestSchema = z.object({
  packId: z.string().max(40),
  /** RF-02.07: consentimiento expreso al suministro inmediato de los créditos. */
  consent: z.boolean(),
});

export const checkoutCreatedSchema = z.object({
  checkoutId: z.uuid(),
  /** Página de pago de Stripe a la que se redirige al usuario. */
  url: z.url(),
});

export const checkoutStatusSchema = z.enum([
  'open',
  'processing',
  'credited',
  'expired',
  'failed',
  'review',
]);

export const checkoutSchema = z
  .object({
    id: z.uuid(),
    packId: z.string(),
    credits: z.number().int(),
    amountCents: z.number().int(),
    currency: z.string(),
    status: checkoutStatusSchema,
    /** Lo cobrado por Stripe con impuestos, cuando se ha pagado. */
    amountTotalCents: z.number().int().nullable(),
    amountTaxCents: z.number().int().nullable(),
    createdAt: z.iso.datetime(),
    completedAt: z.iso.datetime().nullable(),
  })
  .meta({ id: 'Checkout' });

export const adminCheckoutSchema = checkoutSchema.extend({
  userId: z.uuid(),
  stripeSessionId: z.string().nullable(),
  reviewReason: z.string().nullable(),
});

export const webhookEventSchema = z.object({
  id: z.uuid(),
  eventId: z.string(),
  type: z.string(),
  status: z.enum(['pending', 'processed', 'ignored', 'review', 'failed']),
  outcome: z.string(),
  attempts: z.number().int(),
  receivedAt: z.iso.datetime(),
  processedAt: z.iso.datetime().nullable(),
});

/** Pagos que una persona tiene que resolver (RF-02.09). */
export const paymentReviewSchema = z.object({
  checkouts: z.array(adminCheckoutSchema),
  events: z.array(webhookEventSchema),
});
